import { useCallback, useEffect, useState } from 'react';
import { Module, ScopeEnum } from 'src/domain';
import { StyleTimelineOverviewRow } from 'src/domain/table/style-timeline-overview-row';
import { useUnsavedChangesModule } from 'src/hooks/unsaved-changes';
import { useScope } from 'src/hooks/useScope';
import { useStyleTimelineRows } from '../table/useStyleTimelineRows';

export function mergeRowsWithCache(cache: StyleTimelineOverviewRow[], rows: StyleTimelineOverviewRow[]) {
    const cacheMap = new Map(cache.map((r) => [r.rowId, r]));
    return rows.map((row) => cacheMap.get(row.rowId) ?? row);
}

function mergeCacheUpdates(
    previousCache: StyleTimelineOverviewRow[],
    rows: StyleTimelineOverviewRow[]
): StyleTimelineOverviewRow[] {
    const newCache = [...previousCache];

    for (const row of rows) {
        let existingIndex = -1;
        for (let i = 0; i < newCache.length; i++) {
            if (newCache[i].rowId === row.rowId) {
                existingIndex = i;
                break;
            }
        }

        if (existingIndex !== -1) {
            newCache[existingIndex] = row;
        } else {
            newCache.push(row);
        }
    }

    return newCache;
}

export const useStyleTimelineSimulationRows = (selectedStorefrontType?: string) => {
    const { data, collections, salesCampaigns, loading, error } = useStyleTimelineRows(selectedStorefrontType);
    const [unsavedChanges] = useUnsavedChangesModule<StyleTimelineOverviewRow[]>(Module.StyleTimeline);
    const [simulationData, setSimulationData] = useState<StyleTimelineOverviewRow[]>([]);
    const [cache, setCache] = useState<StyleTimelineOverviewRow[]>([]);
    const scope = useScope();
    // dataScope tracks which scope simulationData/cache were built for.
    // When scope !== dataScope, return [] + loading=true synchronously so the
    // grid never sees stale rows from the previous scope (not even for one render).
    const [dataScope, setDataScope] = useState<ScopeEnum | undefined>(scope);
    const scopeMismatch = dataScope !== scope;

    useEffect(() => {
        setSimulationData([]);
        setCache([]);
        setDataScope(scope);
    }, [scope]);

    const updateCache = useCallback((rows: StyleTimelineOverviewRow[]) => {
        setCache((prev) => mergeCacheUpdates(prev, rows));
    }, []);

    useEffect(() => {
        const merged = mergeRowsWithCache(cache, data);
        if (unsavedChanges) {
            const changesMap = new Map(unsavedChanges.map((r) => [r.rowId, r]));
            setSimulationData(merged.map((row) => ({ ...(changesMap.get(row.rowId) ?? row) })));
        } else {
            setSimulationData(merged.map((row) => ({ ...row })));
        }
    }, [cache, data, unsavedChanges]);

    return {
        data: scopeMismatch ? [] : simulationData,
        collections,
        salesCampaigns,
        loading: loading || scopeMismatch,
        error,
        updateCache,
    };
};
